import {
  AdminApiClient,
  ApiError,
  isRecord,
  unwrapList,
  type AdminCredentials,
  type JsonRecord,
} from './api-client';

/** View mode only. This value must never reach a legacy business mutation endpoint. */
export const AUTHORIZED_SCOPE = '__authorized__';
export interface PageScope {
  appId: string;
  cityId: string;
  page?: number;
  dataset?: string | undefined;
}
export const PAGE_DATASETS: Record<string, string> = {
  'ADM-A02': 'orders',
  'ADM-A03': 'payments',
  'ADM-A04': 'orders',
  'ADM-B01': 'cities',
  'ADM-B03': 'regions',
  'ADM-B04': 'apps',
  'ADM-B09': 'apps',
  'ADM-C01': 'products',
  'ADM-C08': 'products',
  'ADM-C02': 'products',
  'ADM-C03': 'products',
  'ADM-C04': 'products',
  'ADM-C05': 'products',
  'ADM-C06': 'products',
  'ADM-C07': 'products',
  'ADM-C09': 'products',
  'ADM-C10': 'products',
  'ADM-D01': 'cmsPages',
  'ADM-D06': 'cmsPages',
  'ADM-D07': 'cmsPages',
  'ADM-D08': 'cmsPages',
  'ADM-E01': 'orders',
  'ADM-E03': 'orders',
  'ADM-E04': 'orders',
  'ADM-E05': 'orders',
  'ADM-E08': 'refunds',
  'ADM-E09': 'afterSales',
  'ADM-F01': 'members',
  'ADM-F03': 'memberEvents',
  'ADM-F04': 'memberTags',
  'ADM-F05': 'behaviorFunnel',
  'ADM-F06': 'preferences',
  'ADM-F07': 'paymentAbandonment',
  'ADM-F08': 'exportJobs',
  'ADM-F02': 'members',
  'ADM-G01': 'payMerchants',
  'ADM-G02': 'payMerchants',
  'ADM-G06': 'payHealth',
  'ADM-G03': 'payMerchantBindings',
  'ADM-G04': 'payRoutes',
  'ADM-G05': 'payHealth',
  'ADM-G07': 'payments',
  'ADM-G08': 'paymentFailures',
  'ADM-G09': 'refunds',
  'ADM-G10': 'payProtection',
  'ADM-H01': 'couponCampaigns',
  'ADM-H02': 'couponCampaigns',
  'ADM-H03': 'rescueStrategies',
  'ADM-H05': 'channels',
  'ADM-H06': 'channels',
  'ADM-H07': 'promoters',
  'ADM-H08': 'promotionLinks',
  'ADM-H09': 'commissionRules',
  'ADM-H10': 'settlementBatches',
  'ADM-I01': 'messageTemplates',
  'ADM-I02': 'messageTasks',
  'ADM-I03': 'messageSubscriptions',
  'ADM-I04': 'financeProfit',
  'ADM-I05': 'reconciliation',
  'ADM-I06': 'settlementBatches',
  'ADM-I07': 'reports',
  'ADM-I08': 'exportJobs',
  'ADM-J07': 'runtimeConfig',
  'ADM-J08': 'releases',
  'ADM-J09': 'releases',
  'ADM-J10': 'opsRecords',
  'ADM-A06': 'opsRecords',
  'ADM-E10': 'exportJobs',
  'ADM-D02': 'cmsPages',
  'ADM-D03': 'cmsPages',
  'ADM-D04': 'cmsPages',
  'ADM-D05': 'cmsPages',
  'ADM-D09': 'cmsPages',
  'ADM-D10': 'cmsPages',
  'ADM-H04': 'rescueStrategies',
};

const scopedLists: Record<string, string> = {
  orders: 'orders',
  refunds: 'refunds',
  'after-sales': 'afterSales',
  'payment-transactions': 'payments',
  'payment-failures': 'paymentFailures',
  'payment-merchants': 'payMerchants',
  'payment-bindings': 'payMerchantBindings',
  'payment-route-rules': 'payRoutes',
  'merchant-health': 'payHealth',
  'payment-protection-events': 'payProtection',
  'message-tasks': 'messageTasks',
  'message-templates': 'messageTemplates',
  'message-subscriptions': 'messageSubscriptions',
  members: 'members',
  tags: 'memberTags',
  segments: 'memberSegments',
  'member-events': 'memberEvents',
  'member-tags': 'memberTags',
  'member-segments': 'memberSegments',
  preferences: 'preferences',
  'behavior-funnel': 'behaviorFunnel',
  'payment-abandonment': 'paymentAbandonment',
  'coupon-campaigns': 'couponCampaigns',
  'coupon-rules': 'couponGrants',
  'rescue-strategies': 'rescueStrategies',
  channels: 'channels',
  'channel-orgs': 'channelOrgs',
  promoters: 'promoters',
  'promotion-links': 'promotionLinks',
  'commission-rules': 'commissionRules',
  'settlement-batches': 'settlementBatches',
  exports: 'exportJobs',
  'reports/app-city-performance': 'reports',
  'finance/order-profit': 'financeProfit',
  'finance/cost-rules': 'financeCostRules',
  'reconciliation/batches': 'reconciliation',
  regions: 'regions',
  'cms/pages': 'cmsPages',
  'cms/templates': 'cmsTemplates',
  'cms/definitions': 'cmsDefinitions',
  'cms/component-definitions': 'cmsDefinitions',
  releases: 'releases',
};
const resourceKinds: Record<string, string> = {
  orders: 'order',
  refunds: 'refund',
  'after-sales': 'afterSales',
  members: 'member',
  'message-tasks': 'messageTask',
  'coupon-campaigns': 'campaign',
  'coupon-rules': 'couponRule',
  channels: 'channel',
  promoters: 'promoter',
  'promotion-links': 'link',
  'settlement-batches': 'settlement',
  exports: 'export',
};
export function scopeListRequest(
  path: string,
  scope: PageScope,
): { path: string; dataset: string } | null {
  const url = new URL(path, 'https://admin.invalid');
  let dataset: string | undefined;
  const shared = /^\/api\/admin\/m2\/(apps|cities|products)$/u.exec(url.pathname);
  if (shared) dataset = shared[1];
  else {
    const match = /^\/api\/admin\/m[2-8]\/(?:apps\/[^/]+\/)?(.+)$/u.exec(url.pathname);
    if (match) dataset = scopedLists[match[1]!];
  }
  if (!dataset) return null;
  const query = new URLSearchParams(url.search);
  if (query.get('appId') === AUTHORIZED_SCOPE) query.delete('appId');
  if (scope.appId) query.set('appId', scope.appId);
  if (scope.cityId) query.set('cityId', scope.cityId);
  if (scope.dataset === dataset && !query.has('page')) query.set('page', String(scope.page ?? 1));
  return { path: `/api/admin/business-scope/list/${dataset}?${query}`, dataset };
}
export class AuthorizedScopeApiClient extends AdminApiClient {
  private readonly origins = new Map<string, string>();
  getPageScope(): PageScope {
    return { ...this.pageScope() };
  }
  constructor(
    credentials: () => AdminCredentials,
    baseUrl: string,
    onUnauthorized: ((error: ApiError) => void) | undefined,
    private readonly pageScope: () => PageScope,
  ) {
    super(credentials, baseUrl, onUnauthorized);
  }
  override async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const scope = this.pageScope();
    const reading = !init.method || init.method === 'GET';
    if (reading && path === '/api/admin/m8/apps/' + AUTHORIZED_SCOPE + '/security')
      return super.request<T>('/api/admin/m8/security', init);
    // Already explicit app detail/configuration endpoints remain explicit.
    const hasAll = path.includes(AUTHORIZED_SCOPE);
    if (
      reading &&
      (hasAll ||
        Boolean(scope.appId) ||
        /^\/api\/admin\/m2\/(apps|cities|products)(\?|$)/u.test(path))
    ) {
      const mapped = scopeListRequest(path, scope);
      if (mapped) {
        if (
          scope.appId &&
          [
            'preferences',
            'behaviorFunnel',
            'paymentAbandonment',
            'memberEvents',
            'memberDetail',
            'payHealth',
            'payProtection',
          ].includes(mapped.dataset)
        )
          return super.request<T>(
            path.replaceAll(AUTHORIZED_SCOPE, encodeURIComponent(scope.appId)),
            init,
          );
        const result = await super.request<T>(mapped.path, { ...init, cache: 'no-store' });
        if (mapped.dataset === 'orders')
          for (const row of unwrapList(result)) {
            const origin = row.originAppId ?? row.sourceAppId;
            if (typeof row.id === 'string' && typeof origin === 'string')
              this.origins.set('order:' + row.id, origin);
          }
        if (isRecord(result) && typeof window !== 'undefined')
          window.dispatchEvent(
            new CustomEvent('travel:authorized-list', {
              detail: {
                dataset: mapped.dataset,
                appId: scope.appId,
                cityId: scope.cityId,
                page: result.page,
                totalPages: result.totalPages,
                total: result.total,
              },
            }),
          );
        return result;
      }
    }
    if (hasAll) {
      const url = new URL(path, 'https://admin.invalid');
      if (!reading && url.pathname === '/api/admin/m4/orders/batch-deletion') {
        const body: unknown = typeof init.body === 'string' ? JSON.parse(init.body) : null;
        if (
          !isRecord(body) ||
          !Array.isArray(body.orders) ||
          body.orders.length < 1 ||
          body.orders.length > 50 ||
          body.orders.some((item) => !isRecord(item) || typeof item.id !== 'string')
        )
          throw new ApiError(400, 'VALIDATION_FAILED', '请选择1–50个可操作订单。');
        const origins = await Promise.all(
          body.orders.map(async (item: JsonRecord) => {
            const id = String(item.id);
            const cached = this.origins.get('order:' + id);
            if (cached) return cached;
            const context = await super.request<JsonRecord>(
              '/api/admin/business-scope/resource-context/order/' + encodeURIComponent(id),
              { cache: 'no-store', ...(init.signal === undefined ? {} : { signal: init.signal }) },
            );
            if (typeof context.originAppId !== 'string' || !context.originAppId)
              throw new ApiError(
                409,
                'ORDER_CONTEXT_UNAVAILABLE',
                '订单归属未能确认，请刷新列表。',
              );
            return context.originAppId;
          }),
        );
        const unique = [...new Set(origins)];
        if (unique.length !== 1)
          throw new ApiError(
            409,
            'MIXED_APP_BATCH_UNSUPPORTED',
            '选中的订单来自不同小程序，请分别勾选后处理，避免部分成功。',
          );
        return super.request<T>(
          path.replaceAll(AUTHORIZED_SCOPE, encodeURIComponent(unique[0]!)),
          init,
        );
      }
      const member = /^\/api\/admin\/m5\/members\/([^/]+)$/u.exec(url.pathname);
      if (reading && member) {
        const query = new URLSearchParams();
        if (scope.appId) query.set('appId', scope.appId);
        if (scope.cityId) query.set('cityId', scope.cityId);
        return super.request<T>(`/api/admin/business-scope/resource/member/${member[1]}?${query}`, {
          ...init,
          cache: 'no-store',
        });
      }
      const resource = /^\/api\/admin\/m[3-8]\/(?:apps\/[^/]+\/)?([^/]+)\/([^/]+)(?:\/|$)/iu.exec(
        url.pathname,
      );
      const kind = resource ? resourceKinds[resource[1]!] : undefined;
      let appId = scope.appId;
      if (resource && kind) {
        const cached = this.origins.get(kind + ':' + resource[2]);
        if (cached && !appId) appId = cached;
        else {
          const context = await super.request<JsonRecord>(
            `/api/admin/business-scope/resource-context/${kind}/${resource[2]}${appId ? `?appId=${encodeURIComponent(appId)}` : ''}`,
            { cache: 'no-store', ...(init.signal === undefined ? {} : { signal: init.signal }) },
          );
          const origin = context.originAppId;
          if (typeof origin === 'string' && origin) {
            appId = origin;
            this.origins.set(kind + ':' + resource[2], origin);
          }
        }
      }
      if (!appId)
        throw new ApiError(
          409,
          'EXPLICIT_APP_REQUIRED',
          '此操作需要明确小程序，请在本页筛选区选择小程序后继续；不会自动选取第一个小程序。',
        );
      path = path.replaceAll(AUTHORIZED_SCOPE, encodeURIComponent(appId));
    }
    return super.request<T>(path, init);
  }
  override upload<T>(path: string, form: FormData, idempotencyKey: string): Promise<T> {
    if (path.includes(AUTHORIZED_SCOPE)) {
      const appId = this.pageScope().appId;
      if (!appId)
        throw new ApiError(409, 'EXPLICIT_APP_REQUIRED', '上传前请在本页选择图片所属的小程序。');
      path = path.replaceAll(AUTHORIZED_SCOPE, encodeURIComponent(appId));
    }
    return super.upload<T>(path, form, idempotencyKey);
  }
  override async downloadWithMetadata(
    path: string,
    extraHeaders: Record<string, string> = {},
    signal?: AbortSignal,
  ): Promise<{ blob: Blob; headers: Headers }> {
    if (path.includes(AUTHORIZED_SCOPE)) {
      const resource = /\/exports\/([^/?]+)\//u.exec(path);
      let appId = this.pageScope().appId;
      if (resource) {
        const context = await super.request<JsonRecord>(
          `/api/admin/business-scope/resource-context/export/${resource[1]}`,
          { cache: 'no-store', ...(signal ? { signal } : {}) },
        );
        if (typeof context.originAppId === 'string') appId = context.originAppId;
      }
      if (!appId)
        throw new ApiError(
          409,
          'EXPLICIT_APP_REQUIRED',
          '下载前请在本页选择导出任务所属的小程序。',
        );
      path = path.replaceAll(AUTHORIZED_SCOPE, encodeURIComponent(appId));
    }
    return super.downloadWithMetadata(path, extraHeaders, signal);
  }
}

export interface ScopeOptions {
  apps: JsonRecord[];
  cities: JsonRecord[];
  cityFilterSupported: boolean;
}
export function parseScopeOptions(value: unknown): ScopeOptions {
  const row = isRecord(value) ? value : {};
  return {
    apps: unwrapList(row.apps),
    cities: unwrapList(row.cities),
    cityFilterSupported: row.cityFilterSupported === true,
  };
}
